import { useState } from 'react';
import { apiClient } from '../api/client';
import type { User } from '../api/types';
import { formatRuPhoneInput, isValidRuPhone, normalizeRuPhone } from '../utils/phone';

const PICKUP_POINTS = [
  'ПВЗ: ул. Королева, 5',
  'ПВЗ: ул. Ленина, 12',
  'ПВЗ: пр-т Мира, 88',
  'ПВЗ: ул. Гагарина, 24',
];

interface RegisterProps {
  onLogoClick?: () => void;
  onSuccess?: (user: User) => void;
  onCancel?: () => void;
  onLoginClick?: () => void;
}

export default function Register({
  onLogoClick: _onLogoClick,
  onSuccess,
  onCancel,
  onLoginClick,
}: RegisterProps) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    password: '',
    pickup_point: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, phone: formatRuPhoneInput(e.target.value) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.phone.trim() && !formData.email.trim()) {
      setError('Необходимо указать телефон или email');
      return;
    }

    if (formData.phone.trim() && !isValidRuPhone(formData.phone)) {
      setError('Введите телефон в формате +7 (999) 999-99-99');
      return;
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Неверный формат email');
      return;
    }

    if (!formData.pickup_point) {
      setError('Необходимо выбрать пункт выдачи (ПВЗ)');
      return;
    }

    setIsLoading(true);

    try {
      const submitData: {
        first_name?: string;
        last_name?: string;
        phone?: string;
        email?: string;
        password?: string;
        pickup_point?: string;
      } = {};

      if (formData.first_name.trim()) submitData.first_name = formData.first_name.trim();
      if (formData.last_name.trim()) submitData.last_name = formData.last_name.trim();
      if (formData.phone.trim()) submitData.phone = normalizeRuPhone(formData.phone);
      if (formData.email.trim()) submitData.email = formData.email.trim();
      if (formData.password.trim()) submitData.password = formData.password;
      if (formData.pickup_point) submitData.pickup_point = formData.pickup_point;

      const response = await apiClient.createUser(submitData);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Не удалось создать профиль');
      }

      onSuccess?.(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при регистрации');
      console.error('Registration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex-1 bg-[#f3f3f3]">
      <main className="relative mx-auto w-full max-w-[1520px] px-4 pb-[32px] pt-[20px] sm:px-6 lg:px-8">
        <div className="mb-[24px] sm:mb-[32px]">
          <h1 className="text-[30px] font-extrabold leading-none tracking-[1px] text-black sm:text-[36px] lg:text-[40px] lg:tracking-[2px]">
            Регистрация
          </h1>
        </div>

        <div className="mx-auto max-w-[720px]">
          <div className="rounded-[20px] bg-white p-5 shadow-lg sm:p-8 lg:p-10">
            <div className="mb-6 sm:mb-7">
              <p className="text-[16px] leading-[1.5] text-gray-600 sm:text-[18px]">
                Заполните данные профиля. Для быстрого входа в дальнейшем используйте номер телефона.
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-[10px] border border-red-200 bg-red-50 p-[15px]">
                <p className="text-[15px] text-red-600 sm:text-[16px]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="first_name" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                    Имя
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full rounded-[12px] border-2 border-gray-300 px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                    placeholder="Введите имя"
                  />
                </div>

                <div>
                  <label htmlFor="last_name" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                    Фамилия
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full rounded-[12px] border-2 border-gray-300 px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                    placeholder="Введите фамилию"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                  Телефон <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputMode="tel"
                  autoComplete="tel"
                  className="w-full rounded-[12px] border-2 border-gray-300 px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                  placeholder="+7 (999) 999-99-99"
                  maxLength={18}
                />
                <p className="mt-2 text-[13px] leading-[1.45] text-gray-500 sm:text-[14px]">
                  Укажите телефон в формате <code>+7 (999) 999-99-99</code>. Телефон или email обязателен.
                </p>
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-[12px] border-2 border-gray-300 px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                  placeholder="example@mail.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                  Пароль
                </label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full rounded-[12px] border-2 border-gray-300 px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                  placeholder="Введите пароль"
                />
                <p className="mt-2 text-[13px] leading-[1.45] text-gray-500 sm:text-[14px]">
                  Если пароль не указан, система может создать временный пароль.
                </p>
              </div>

              <div>
                <label htmlFor="pickup_point" className="mb-2 block text-[16px] font-semibold text-black sm:text-[18px]">
                  Пункт выдачи (ПВЗ)
                </label>
                <select
                  id="pickup_point"
                  value={formData.pickup_point}
                  onChange={(e) => setFormData({ ...formData, pickup_point: e.target.value })}
                  required
                  className="w-full rounded-[12px] border-2 border-gray-300 bg-white px-4 py-3 text-[16px] focus:border-[#5d76cb] focus:outline-none sm:text-[18px]"
                >
                  <option value="">Выберите ПВЗ</option>
                  {PICKUP_POINTS.map((point) => (
                    <option key={point} value={point}>
                      {point}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-[15px]">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 rounded-[12px] bg-[#5d76cb] py-[14px] text-[18px] font-semibold text-white transition-all duration-200 hover:bg-[#4d66bb] disabled:cursor-not-allowed disabled:opacity-50 sm:text-[20px]"
                >
                  {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
                </button>
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isLoading}
                    className="rounded-[12px] bg-gray-200 px-[24px] py-[14px] text-[18px] font-semibold text-gray-700 transition-all duration-200 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 sm:text-[20px]"
                  >
                    Отмена
                  </button>
                )}
              </div>

              {onLoginClick && (
                <div className="border-t border-gray-200 pt-[18px] text-center">
                  <p className="mb-[6px] text-[15px] text-gray-600 sm:text-[16px]">Уже есть аккаунт?</p>
                  <button
                    type="button"
                    onClick={onLoginClick}
                    className="text-[15px] font-semibold text-[#5d76cb] hover:underline sm:text-[16px]"
                  >
                    Перейти ко входу
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
